import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { usePageTitle } from '@/hooks/use-page-title'
import { signIn } from '@/lib/auth-client'

const LoginSchema = z.object({
	username: z.string().min(1, 'Username or email is required'),
	password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof LoginSchema>

export function LoginPage() {
	usePageTitle('Login')
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo') || '/admin'
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isGoogleLoading, setIsGoogleLoading] = useState(false)

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormData>({
		// zodResolver's types brand against zod v4 core minor: 0 while our zod
		// ships with minor: 3; runtime is fine, cast silences the mismatch.
		resolver: zodResolver(LoginSchema as never),
	})

	const signInWithGoogle = async () => {
		setIsGoogleLoading(true)
		setError(null)
		try {
			const absoluteCallback = redirectTo.startsWith('http')
				? redirectTo
				: `${window.location.origin}${redirectTo.startsWith('/') ? '' : '/'}${redirectTo}`

			await signIn.social({
				provider: 'google',
				callbackURL: absoluteCallback,
				errorCallbackURL: `${window.location.origin}/access-denied`,
			})
		} catch (_err) {
			setError('Could not start Google sign-in.')
			setIsGoogleLoading(false)
		}
	}

	const onSubmit = async (data: LoginFormData) => {
		setIsLoading(true)
		setError(null)

		try {
			const usernameResult = await signIn.username({
				username: data.username,
				password: data.password,
			})

			if (usernameResult.error && data.username.includes('@')) {
				const emailResult = await signIn.email({
					email: data.username,
					password: data.password,
				})
				if (emailResult.error) {
					setError(emailResult.error.message || 'Login failed')
					return
				}
			} else if (usernameResult.error) {
				setError(usernameResult.error.message || 'Login failed')
				return
			}

			navigate(redirectTo)
		} catch (_err) {
			setError('An error occurred. Please try again.')
		} finally {
			setIsLoading(false)
		}
	}

	const anyLoading = isLoading || isGoogleLoading

	return (
		<div className="min-h-screen flex flex-col bg-white">
			{/* Top bar */}
			<div className="border-b border-gray-200 bg-white">
				<div className="max-w-screen-md mx-auto px-6 py-4 flex items-center justify-between">
					<img src="/agnis_logo_crop.png" alt="Agnis" className="h-8" />
					<div className="flex items-center gap-2 text-xs text-gray-400">
						<ShieldCheck className="h-3.5 w-3.5" />
						<span>Zabezpečené přihlášení</span>
					</div>
				</div>
			</div>

			{/* Main */}
			<div className="flex-1 flex items-center justify-center px-4 py-12">
				<div className="w-full max-w-sm">
					{/* Heading */}
					<div className="text-center mb-8">
						<h1 className="text-2xl font-bold tracking-tight text-gray-900">
							Přihlášení do administrace
						</h1>
						<p className="text-sm text-gray-500 mt-2">
							Vítejte zpět. Přihlaste se pro pokračování.
						</p>
					</div>

					{/* Google */}
					<button
						type="button"
						onClick={signInWithGoogle}
						disabled={anyLoading}
						className="w-full h-11 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm font-medium flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
					>
						{isGoogleLoading ? (
							<>
								<LoadingSpinner size="sm" />
								<span>Přesměrování...</span>
							</>
						) : (
							<>
								<GoogleColorIcon className="h-5 w-5" />
								<span>Pokračovat přes Google</span>
							</>
						)}
					</button>

					{/* Divider */}
					<div className="relative my-6">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t border-gray-200" />
						</div>
						<div className="relative flex justify-center">
							<span className="bg-white px-3 text-xs uppercase tracking-wider text-gray-400">
								nebo
							</span>
						</div>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
						<div className="space-y-2">
							<label
								htmlFor="username"
								className="text-sm font-medium text-gray-700"
							>
								Uživatelské jméno nebo e-mail
							</label>
							<Input
								id="username"
								type="text"
								autoComplete="username"
								placeholder="vas.email@priklad.cz"
								className="h-11 bg-white border-gray-300 text-gray-900 focus-visible:ring-red-500"
								{...register('username')}
							/>
							{errors.username && (
								<p className="text-red-600 text-sm">
									{errors.username.message}
								</p>
							)}
						</div>

						<div className="space-y-2">
							<label
								htmlFor="password"
								className="text-sm font-medium text-gray-700"
							>
								Heslo
							</label>
							<Input
								id="password"
								type="password"
								autoComplete="current-password"
								placeholder="••••••••"
								className="h-11 bg-white border-gray-300 text-gray-900 focus-visible:ring-red-500"
								{...register('password')}
							/>
							{errors.password && (
								<p className="text-red-600 text-sm">
									{errors.password.message}
								</p>
							)}
						</div>

						{error && (
							<div className="rounded-lg bg-red-50 border border-red-200 p-3">
								<p className="text-red-700 text-sm">{error}</p>
							</div>
						)}

						<Button
							type="submit"
							className="w-full h-11"
							disabled={anyLoading}
						>
							{isLoading ? (
								<>
									<LoadingSpinner size="sm" className="mr-2" />
									Přihlašování...
								</>
							) : (
								<>
									Přihlásit se
									<ArrowRight className="ml-2 h-4 w-4" />
								</>
							)}
						</Button>
					</form>

					{/* Register link */}
					<p className="mt-6 text-center text-sm text-gray-500">
						Nemáte účet?{' '}
						<Link
							to="/register"
							className="text-red-600 font-medium hover:text-red-700 hover:underline"
						>
							Registrujte se
						</Link>
					</p>

					{/* Footer note */}
					<p className="mt-4 text-center text-xs text-gray-400">
						Přístup pouze pro autorizované administrátory.
					</p>
				</div>
			</div>
		</div>
	)
}

function GoogleColorIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 48 48"
			aria-hidden="true"
		>
			<path
				fill="#FFC107"
				d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
			/>
			<path
				fill="#FF3D00"
				d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
			/>
			<path
				fill="#4CAF50"
				d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
			/>
			<path
				fill="#1976D2"
				d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
			/>
		</svg>
	)
}
